import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartFilledIcon } from '@heroicons/react/24/solid';
import { useFetcher } from '@remix-run/react';
import cx from 'classnames';

import LoginOrSubscribe from '~/components/CTAs/LoginOrSubscribe';
import SlideUpModal from '~/components/Modal/SlideUpModal';
import type { Admin } from '~/interfaces/admin';
import type { CommentType } from '~/interfaces/comment';
import type { Subscription } from '~/interfaces/subscriptions';

interface Props {
  postId?: string;
  comment?: CommentType;
  subscription?: Subscription;
  admin?: Admin;
}

const LikeCounter = ({
  children,
  count = 0,
  liked,
}: {
  children: React.ReactNode;
  count?: number;
  liked?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div className="group flex items-center space-x-1">
      {children}
      <div className="flex items-center space-x-0.5">
        <span
          className={cx(
            liked ? 'opacity-100' : 'text-wt-text-on-background-color opacity-60 group-hover:opacity-100',
            'text-xs uppercase',
          )}
        >
          {t('comments.like')}
        </span>
        {count === 0 ? null : (
          <span
            className={cx(
              liked ? 'opacity-100' : 'opacity-60',
              'text-wt-text-on-background-color -mt-[1px] text-xs group-hover:opacity-100',
            )}
          >
            ({count})
          </span>
        )}
      </div>
    </div>
  );
};

const getIsLikedId = (admin?: Admin, subcription?: Subscription) => {
  if (admin) {
    return admin?.id;
  }

  if (subcription) {
    return subcription?.id;
  }

  return '';
};

const ICON_CLASSES = 'h-4 w-4 text-wt-text-on-background-color opacity-60 group-hover:opacity-100';

const CommentLikeAction = ({ postId, comment, subscription, admin }: Props) => {
  const { t } = useTranslation();
  const [likedIds] = useState<string[]>(comment?.likes || []);
  const [liked, setLiked] = useState(likedIds.includes(getIsLikedId(admin, subscription)));
  const [commentLikeCount, setCommentLikeCount] = useState(likedIds?.length || 0);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetcher = useFetcher();
  const isAdminLoggedIn = Boolean(admin);
  const isSubcriberLoggedIn = Boolean(subscription);
  const isNotLoggedIn = !isAdminLoggedIn && !isSubcriberLoggedIn;

  const handleConditionalRendering = () => {
    setLiked(!liked);

    if (liked) {
      setCommentLikeCount(commentLikeCount - 1);
    } else {
      setCommentLikeCount(commentLikeCount + 1);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    if (isAdminLoggedIn) {
      fetcher.submit(formData, {
        method: 'post',
        action: '/admins/comment_like',
      });
    } else {
      fetcher.submit(formData, { method: 'post', action: '/comment_like' });
    }

    handleConditionalRendering();
  };

  if (isNotLoggedIn) {
    return (
      <>
        <button className="-mt-0.5" type="button" onClick={() => setIsModalOpen(true)}>
          <LikeCounter count={commentLikeCount}>
            <HeartIcon className={ICON_CLASSES} />
          </LikeCounter>
        </button>
        {isModalOpen && (
          <SlideUpModal
            title={t('gated.login_or_subscribe')}
            description=""
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          >
            <div>
              <LoginOrSubscribe />
            </div>
          </SlideUpModal>
        )}
      </>
    );
  }

  return (
    <div className="flex items-start">
      <div className="min-w-0 flex-1">
        <fetcher.Form
          method="post"
          onSubmit={handleSubmit}
          action={isAdminLoggedIn ? '/admins/comment_like' : '/comment_like'}
          className="relative"
        >
          <input type="hidden" name="post_id" value={postId} />
          <input type="hidden" name="comment_id" value={comment?.id} />
          <input type="hidden" name="subscription_id" value={subscription?.id} />
          <input type="hidden" name="user_id" value={admin?.id} />
          <button type="submit">
            {liked ? (
              <LikeCounter count={commentLikeCount} liked>
                <HeartFilledIcon className="h-4 w-4 text-wt-primary" />
              </LikeCounter>
            ) : (
              <LikeCounter count={commentLikeCount}>
                <HeartIcon className={ICON_CLASSES} />
              </LikeCounter>
            )}
          </button>
        </fetcher.Form>
      </div>
    </div>
  );
};

export default CommentLikeAction;
